import { getCurrentUser } from '@/lib/auth-utils'
import { fetchAccessibleCompanyIds } from '@/lib/company-access'
import { ROLE_ADMIN } from '@/lib/permissions'
import type { Property } from '@/models'
import { neon } from '@/services/neon'

type PropertyRow = Omit<Property, 'amenities'> & {
  amenities?: string[] | null
}

function normalizeProperty(row: PropertyRow): Property {
  return {
    ...row,
    amenities: row.amenities || [],
  }
}

function validatePropertyTime(value: unknown, field: string): void {
  if (value === undefined || value === null || value === '') return
  if (typeof value !== 'string' || !/^\d{2}:\d{2}$/.test(value))
    throw new Error(`${field} must be HH:MM`)
  const [h, m] = value.split(':').map(Number)
  if (h < 0 || h > 23 || m < 0 || m > 59) throw new Error(`${field} must be HH:MM`)
}

export async function fetchProperties(): Promise<Property[]> {
  const currentUser = getCurrentUser()

  if (!currentUser || currentUser.role === ROLE_ADMIN) {
    const { data, error } = await neon
      .from('properties')
      .select('*')
      .is('deleted_at', null)
      .order('created_at', { ascending: false })
    if (error) {
      throw new Error(error.message)
    }
    return ((data as PropertyRow[]) || []).map(normalizeProperty)
  }

  if (!currentUser.id) {
    return []
  }

  const companyIds = await fetchAccessibleCompanyIds(currentUser.id)
  if (companyIds.length === 0) {
    return []
  }

  const { data, error } = await neon
    .from('properties')
    .select('*')
    .is('deleted_at', null)
    .in('company_id', companyIds)
    .order('created_at', { ascending: false })

  if (error) {
    throw new Error(error.message)
  }

  return ((data as PropertyRow[]) || []).map(normalizeProperty)
}

export async function fetchPropertyById(id: string): Promise<Property | null> {
  const { data, error } = await neon
    .from('properties')
    .select('*')
    .eq('id', id)
    .is('deleted_at', null)
    .single()

  if (error) {
    const isNotFound = error.code === 'PGRST116' || error.details?.includes('0 rows')
    if (isNotFound) {
      return null
    }
    throw new Error(error.message)
  }

  const property = normalizeProperty(data as PropertyRow)
  const currentUser = getCurrentUser()

  if (!currentUser || currentUser.role === ROLE_ADMIN) {
    return property
  }

  const companyIds = await fetchAccessibleCompanyIds(currentUser.id || '')
  if (!companyIds.includes(property.company_id)) {
    throw new Error('You do not have permission to view this property.')
  }

  return property
}

export async function createProperty(input: Partial<Property>): Promise<Property> {
  validatePropertyTime((input as any).check_in_time, 'check_in_time')
  validatePropertyTime((input as any).check_out_time, 'check_out_time')
  const currentUser = getCurrentUser()
  const companyId = input.company_id

  // Billing tier check (behind flag — polished but not enabled)
  const { BILLING_ENABLED, canCreatePropertyForTier, getTierForPropertyCount } = await import(
    '@/lib/billing'
  )
  if (BILLING_ENABLED && companyId) {
    const existing = await fetchProperties()
    const count = existing.filter((p) => p.company_id === companyId).length
    const tier = getTierForPropertyCount(count)
    const check = canCreatePropertyForTier(count, tier)
    if (!check.allowed) throw new Error(check.reason)
  }

  if (!companyId) {
    throw new Error('Property must be assigned to a company.')
  }

  if (!currentUser?.id) {
    throw new Error('User not authenticated.')
  }

  if (currentUser.role !== ROLE_ADMIN) {
    const companyIds = await fetchAccessibleCompanyIds(currentUser.id || '')
    if (!companyIds.includes(companyId)) {
      throw new Error('You do not have permission to add properties for this company.')
    }
  }

  const { data, error } = await neon
    .from('properties')
    .insert({ ...input, created_by: currentUser?.id || null })
    .select()
    .single()

  if (error) {
    throw new Error(error.message)
  }

  return normalizeProperty(data as PropertyRow)
}

export async function updateProperty(id: string, input: Partial<Property>): Promise<Property> {
  validatePropertyTime((input as any).check_in_time, 'check_in_time')
  validatePropertyTime((input as any).check_out_time, 'check_out_time')
  const existing = await fetchPropertyById(id)
  if (!existing) {
    throw new Error('Property not found.')
  }

  const currentUser = getCurrentUser()
  if (currentUser && currentUser.role !== ROLE_ADMIN) {
    const companyId = input.company_id || existing.company_id
    const { data, error } = await neon
      .from('company_members')
      .select('role')
      .eq('company_id', companyId)
      .eq('user_id', currentUser.id)
      .eq('is_active', true)
      .single()

    const membershipRole = !error ? (data as { role?: string } | null)?.role : null
    if (membershipRole !== 'owner' && membershipRole !== 'manager') {
      throw new Error('You do not have permission to edit this property.')
    }
  }

  // TOCTOU fix: conditional write verifies company_id + not soft-deleted at write time
  const { data, error } = await neon
    .from('properties')
    .update(input)
    .eq('id', id)
    .eq('company_id', existing.company_id)
    .is('deleted_at', null)
    .select()
    .single()

  if (error) {
    const isNotFound =
      (error as any).code === 'PGRST116' || (error as any).details?.includes('0 rows')
    if (isNotFound) {
      throw new Error('Property not found or permission revoked — please refresh.')
    }
    throw new Error(error.message)
  }

  return normalizeProperty(data as PropertyRow)
}

export async function deleteProperty(id: string): Promise<void> {
  const existing = await fetchPropertyById(id)
  if (!existing) {
    return
  }

  const currentUser = getCurrentUser()
  if (currentUser && currentUser.role !== ROLE_ADMIN) {
    const { data, error } = await neon
      .from('company_members')
      .select('role')
      .eq('company_id', existing.company_id)
      .eq('user_id', currentUser.id)
      .eq('is_active', true)
      .single()

    const membershipRole = !error ? (data as { role?: string } | null)?.role : null
    if (membershipRole !== 'owner' && membershipRole !== 'manager') {
      throw new Error('You do not have permission to delete this property.')
    }
  }

  // Soft delete: set deleted_at + conditional write for TOCTOU
  const { error, data } = await (neon.from('properties') as any)
    .update({ deleted_at: new Date().toISOString() })
    .eq('id', id)
    .eq('company_id', existing.company_id)
    .is('deleted_at', null)
    .select()
    .single()
  if (error) {
    const isNotFound =
      (error as any).code === 'PGRST116' || (error as any).details?.includes('0 rows')
    if (isNotFound) return // already deleted or revoked — idempotent
    throw new Error(error.message)
  }
  if (!data) return
}
